// ---------------------------------------------------------------------------
// Allocation summary (source population reference + running totals)
// ---------------------------------------------------------------------------

export interface SourcePopulationInfo {
    fishGroupId: number;
    /** Known initial population at FG creation. Null if not tracked or unknown. */
    initialPopulationSize: number | null;
}

export interface AllocationSummary {
    /**
     * Sum of every source's initialPopulationSize. Null when at least one
     * source's population is unknown — partial sums are misleading so we
     * collapse to "unknown" rather than showing a wrong total.
     */
    totalSourcePopulation: number | null;
    /** Convenience flag the UI uses to show "—" or a "(?)" hint. */
    anySourceUnknown: boolean;
    /** Sum of valid positive estimatedFishCount values across destinations. */
    totalAllocated: number;
    /** totalSourcePopulation − totalAllocated when ≥ 0; null when source pop unknown. */
    remaining: number | null;
    /** totalAllocated − totalSourcePopulation when > 0; null otherwise. */
    overage: number | null;
}

export interface ComputeAllocationSummaryInput {
    sources: { fishGroupId: number }[];
    destinations: { estimatedFishCount: string }[];
    populationById: Map<number, SourcePopulationInfo>;
}

/**
 * Reduce wizard state to the numbers the AllocationSummary card shows.
 * Mode-agnostic: the four operation modes all need the same primitives
 * (combined source pool, what's been allocated, what's left or in excess),
 * so we keep this as a single function and let the UI label things
 * differently per mode.
 *
 * Population is "initial" — derived from `FishGroup.initialPopulationSize`
 * fetched via the detail endpoint. A future iteration can replace this with
 * a "last known from events" value; the shape returned here would not change.
 */
export function computeAllocationSummary(input: ComputeAllocationSummaryInput): AllocationSummary {
    const { sources, destinations, populationById } = input;

    let totalKnown = 0;
    let anySourceUnknown = false;
    for (const s of sources) {
        const pop = populationById.get(s.fishGroupId)?.initialPopulationSize ?? null;
        if (pop === null) {
            anySourceUnknown = true;
        } else {
            totalKnown += pop;
        }
    }

    let totalAllocated = 0;
    for (const d of destinations) {
        const n = parseAllocationCount(d.estimatedFishCount);
        if (n !== null) totalAllocated += n;
    }

    const totalSourcePopulation = anySourceUnknown ? null : totalKnown;

    let remaining: number | null = null;
    let overage: number | null = null;
    if (totalSourcePopulation !== null) {
        const diff = totalSourcePopulation - totalAllocated;
        if (diff >= 0) {
            remaining = diff;
        } else {
            overage = -diff;
        }
    }

    return {
        totalSourcePopulation,
        anySourceUnknown,
        totalAllocated,
        remaining,
        overage,
    };
}

/**
 * Parse an `estimatedFishCount` form value into a positive integer, or
 * `null` if the string is empty or non-numeric. Exposed because a couple of
 * step-specific components also need to interpret these inputs the same way.
 */
export function parseAllocationCount(value: string): number | null {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const n = Number(trimmed);
    return Number.isFinite(n) && n > 0 ? n : null;
}
